/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type DataType from '@linc-technologies/linced-common/enums/data-type';
import type InputType from '@linc-technologies/linced-common/enums/input-type';
import type PersonType from '@linc-technologies/linced-common/enums/person-type';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import type FieldOption from '@linc-technologies/linced-common/models/people-schema/field-option';
import type Group from '@linc-technologies/linced-common/models/people-schema/group';
import type School from '@linc-technologies/linced-common/models/school';
import { toCamelCase } from '@linc-technologies/linced-common/utils/strings';

// Proto: people_schema.Field.
export default class Field extends Model {
  declare [Type]: 'people-schema/field';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('boolean', { defaultValue: false })
  declare isVisibleToParents: boolean;

  @attr('boolean', { defaultValue: false })
  declare isSiblingLinked: boolean;

  @attr('number', { defaultValue: 0 })
  declare personType: EnumValue<typeof PersonType> | 0;

  @attr('string', { defaultValue: '' })
  declare key: string;

  @attr('number', { defaultValue: 0 })
  declare inputType: EnumValue<typeof InputType> | 0;

  @attr('number', { defaultValue: 0 })
  declare dataType: EnumValue<typeof DataType> | 0;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @fragment('dynamic-field/value')
  declare default: Value | null;

  @attr('string', { defaultValue: '' })
  declare placeholder: string;

  @attr('boolean', { defaultValue: false })
  declare isRequired: boolean;

  @fragmentArray('people-schema/field-option')
  declare options: FragmentArray<FieldOption>;

  @attr('boolean', { defaultValue: false })
  declare optionsFromEnum: boolean;

  @attr({ defaultValue: () => ({}) })
  declare htmlAttributes: Record<string, string>;

  @belongsTo('people-schema/group', { async: true, inverse: 'fields' })
  declare groupId: AsyncBelongsTo<Group>;

  @alias('groupId')
  declare group: AsyncBelongsTo<Group>;

  @attr('string', { defaultValue: '' })
  declare templateId: string;

  // camelCaseLabel is used to help match fields in bulk imported data.
  @computed('label')
  get camelCaseLabel() {
    return toCamelCase(this.label.toLowerCase());
  }

  // requiredRole specifies the Field Access role required to view this field,
  // if applicable.  If empty, anyone with access to the entity can see it.
  @attr('string', { defaultValue: '' })
  declare requiredRole: string;

  // isReadOnly specifies whether this field is read-only.
  @attr('boolean', { defaultValue: false })
  declare isReadOnly: boolean;

  // studentPermission specifies whether this field is available for the Calendar & Excursions feature
  @attr('boolean', { defaultValue: false })
  declare studentPermission: boolean;
}
